import api, { type ApiResponse } from "./api";
import type { PaymentProvider } from "./payments";

export type RenewalQuote = {
  applicationId: string;
  applicationName: string;
  currentExpiry: string | null;
  renewalNumber: number;
  currentAnnualPricePaise: number;
  nextRenewalPricePaise: number;
  currency: string;
  durationDays: number;
};

export type RenewalProviderAvailability = {
  available: boolean;
  displayName: string;
};

export type RenewalProviderAvailabilityMap = {
  stripe: RenewalProviderAvailability;
  razorpay: RenewalProviderAvailability;
};

export type RenewalTransaction = {
  paymentId: string;
  applicationId: string;
  amountPaise: number;
  currency: string;
  renewalNumber: number;
  status: "pending" | "processing";
  provider: PaymentProvider;
  checkoutUrl: string | null;
  providerAvailable: boolean;
  lifecycleStatus: "CHECKOUT_CREATED" | "PENDING";
  providerOrderId: string | null;
  razorpayKeyId: string | null;
};

export type PublisherRenewalTransactionStatus = {
  transactionId: string;
  applicationId: string;
  applicationName: string;
  provider: PaymentProvider;
  status:
    | "PENDING"
    | "CHECKOUT_CREATED"
    | "PAYMENT_PENDING"
    | "VERIFIED"
    | "FAILED"
    | "CANCELLED"
    | "EXPIRED"
    | "APPLIED";
  amountPaise: number;
  amount: string;
  currency: string;
  renewalNumber: number | null;
  currentExpiry: string | null;
  newExpiry: string | null;
  renewalCompleted: boolean;
  verificationComplete: boolean;
  message: string;
  failureReason: string | null;
};

export function formatInrPaise(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export async function fetchRenewalQuote(
  applicationId: string,
): Promise<RenewalQuote> {
  const response = await api.get<ApiResponse<{ quote: RenewalQuote }>>(
    `/publisher/apps/${applicationId}/renewal-quote`,
  );

  return response.data.data.quote;
}

export async function fetchRenewalProviderAvailability(
  applicationId: string,
): Promise<{ providers: RenewalProviderAvailabilityMap; anyAvailable: boolean }> {
  const response = await api.get<
    ApiResponse<{ providers: RenewalProviderAvailabilityMap; anyAvailable: boolean }>
  >(`/publisher/apps/${applicationId}/renewal-providers`);

  return response.data.data;
}

export async function createRenewalIntent(
  applicationId: string,
  provider: PaymentProvider,
): Promise<RenewalTransaction> {
  const response = await api.post<ApiResponse<{ transaction: RenewalTransaction }>>(
    `/publisher/apps/${applicationId}/renewal-intent`,
    { provider },
  );

  return response.data.data.transaction;
}

export async function fetchPublisherRenewalStatus(
  transactionId: string,
): Promise<PublisherRenewalTransactionStatus> {
  const response = await api.get<
    ApiResponse<{ status: PublisherRenewalTransactionStatus }>
  >(`/publisher/renewals/${transactionId}/status`);

  return response.data.data.status;
}

export async function verifyRazorpayRenewalPayment(input: {
  paymentId: string;
  orderId: string;
  razorpayPaymentId: string;
  signature: string;
}): Promise<{ verified: boolean; applied: boolean }> {
  const response = await api.post<
    ApiResponse<{ verification: { verified: boolean; applied: boolean } }>
  >("/payments/razorpay/verify", input);

  return response.data.data.verification;
}

export function renewalStatusHeading(status: PublisherRenewalTransactionStatus["status"]): string {
  switch (status) {
    case "APPLIED":
      return "Renewal applied";
    case "VERIFIED":
      return "Payment verified";
    case "FAILED":
      return "Payment failed";
    case "CANCELLED":
      return "Payment cancelled";
    case "EXPIRED":
      return "Checkout expired";
    case "CHECKOUT_CREATED":
    case "PAYMENT_PENDING":
      return "Verification in progress";
    case "PENDING":
    default:
      return "Payment pending";
  }
}
